import type { InboundIntegration, OutboundIntegrationAction, WorkflowStepRequest } from '../../services/tenantApi';

export const WORKFLOW_TRIGGER_TYPES = [
  { value: 'AlertCreated', label: 'Alert Created' },
  { value: 'AlertUpdated', label: 'Alert Updated' },
  { value: 'AlertClosed', label: 'Alert Closed' },
  { value: 'AlertReopened', label: 'Alert Reopened' },
  { value: 'AlertAcknowledged', label: 'Alert Acknowledged' },
  { value: 'AlertPriorityChanged', label: 'Priority Changed' },
  { value: 'AlertEscalated', label: 'Alert Escalated' },
  { value: 'NotificationReceived', label: 'Notification Received' },
  { value: 'Manual', label: 'Manual' },
];

export type WorkflowSource = { triggerType: string; inboundIntegrationId?: string | null };

export function isNotificationWorkflow(source: WorkflowSource, integration?: InboundIntegration): boolean {
  return source.triggerType.toLowerCase() === 'notificationreceived' || integration?.nonAlert === true;
}

export function getWorkflowTriggerChoices(integration?: InboundIntegration) {
  return WORKFLOW_TRIGGER_TYPES.filter(trigger => trigger.value === 'Manual' ||
    (integration?.nonAlert === true ? trigger.value === 'NotificationReceived' : trigger.value.startsWith('Alert')));
}

export function getWorkflowAssociationChange(original: string | null | undefined, selected: string) {
  const next = selected || null;
  return (original ?? null) === next ? {} : { inboundIntegrationId: next };
}

export function isNotificationSafeStep(stepType: string): boolean {
  return !['updatealert', 'escalate', 'createticket'].includes(stepType.toLowerCase());
}

export function isNotificationSafeAction(action: { actionKey?: string; ticketIdVariable?: string }): boolean {
  return !['createticket', 'updateticket', 'closeticket'].includes((action.actionKey || '').trim().toLowerCase()) &&
    !action.ticketIdVariable?.trim();
}

export function validateWorkflowSource(
  source: WorkflowSource,
  integrations: InboundIntegration[],
  steps: WorkflowStepRequest[] = [],
  actions?: Record<string, OutboundIntegrationAction[]>,
): string | null {
  const integration = integrations.find(item => item.inboundIntegrationId === source.inboundIntegrationId);
  const notificationTrigger = source.triggerType.toLowerCase() === 'notificationreceived';
  if (source.inboundIntegrationId && !integration) return 'Inbound integration is unavailable. Choose an integration or explicitly clear the association.';
  if (notificationTrigger && !source.inboundIntegrationId) return 'Notification Received requires a specific inbound integration.';
  if (integration && !!integration.nonAlert !== notificationTrigger && source.triggerType.toLowerCase() !== 'manual') {
    return 'Trigger is incompatible with the inbound integration type. Choose a compatible trigger.';
  }
  for (const step of steps.filter(item => item.stepType === 'DispatchOutboundDelivery')) {
    const config = step.config || {};
    const action = actions?.[config.integrationId]?.find(item => item.outboundIntegrationActionId === config.integrationActionId);
    if (!action?.isDeliveryCapable || !action.isEnabled) return 'Select an enabled, explicitly delivery-capable action.';
    const inputs = action.deliveryInputs || [];
    if (!inputs.length) return 'The delivery action has no declared inputs. Update its definition.';
    const declared = new Set(inputs.map(field => field.name));
    if (Object.keys(config.fieldMappings || {}).some(name => !declared.has(name))) return 'Delivery mappings contain a removed action field. Reload the action.';
    for (const field of inputs) {
      const value = String(config.fieldMappings?.[field.name] || '').trim();
      if (field.required && (!value || value === 'static:')) return `Delivery input '${field.name}' requires a mapping or literal value.`;
    }
  }
  if (!isNotificationWorkflow(source, integration)) return null;
  for (const step of steps) {
    if (!isNotificationSafeStep(step.stepType)) return `Notification workflows cannot use ${step.stepType}. Remove or replace the step explicitly.`;
    if (!['integrationaction', 'dispatchoutbounddelivery'].includes(step.stepType.toLowerCase())) continue;
    const config = step.config || {};
    const action = config.integrationActionId && actions
      ? actions[config.integrationId]?.find(item => item.outboundIntegrationActionId === config.integrationActionId)
      : undefined;
    if (config.integrationActionId && actions && !action) return 'The selected integration action is unavailable; notification safety cannot be verified.';
    if (!isNotificationSafeAction(action || config)) return 'Notification-linked ticket actions are not supported. Choose a non-ticket action.';
  }
  return null;
}